"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  useShallowSearchParam,
  setShallowSearchParams,
} from "@/lib/hooks/use-shallow-search-param";
import { trackEvent } from "@/lib/analytics";
import type { CrewSeasonType } from "@/lib/crews/types";
import type { DirectoryRow } from "./page";

type Props = {
  rows: DirectoryRow[];
  candidateCount: number;
  loadError?: boolean;
};

const SEASON_LABELS: Record<string, string> = {
  all: "All",
  winter: "Snow",
  summer: "Sun",
  festivals: "Festivals",
};

const SEASON_ORDER: readonly CrewSeasonType[] = ["winter", "summer", "festivals"];

const SECTION_LABEL: Record<CrewSeasonType, string> = {
  winter: "Snow",
  summer: "Sun",
  festivals: "Festivals",
};

export function CrewDirectoryView({ rows, candidateCount, loadError = false }: Props) {
  const seasonFilter = useShallowSearchParam("season") ?? "all";
  const yearFilter = useShallowSearchParam("year") ?? "all";
  const qParam = useShallowSearchParam("q") ?? "";

  const [query, setQuery] = useState(qParam);

  // Keep local query state in sync if URL changes externally
  useEffect(() => {
    setQuery(qParam);
  }, [qParam]);

  // PostHog analytics -- fires on mount + on filter changes
  useEffect(() => {
    trackEvent("crew_directory_viewed", {
      season_filter: seasonFilter,
      year_filter: yearFilter,
      result_count: rows.length,
    });
  }, [seasonFilter, yearFilter, rows.length]);

  // Deduplicated, sorted year labels from rows
  const yearOptions = useMemo(() => {
    const ys = new Set<string>();
    for (const r of rows) ys.add(r.season_label);
    return Array.from(ys).sort();
  }, [rows]);

  const setSeason = (s: string) =>
    setShallowSearchParams({ season: s === "all" ? null : s });

  const setYear = (y: string) =>
    setShallowSearchParams({ year: y === "all" ? null : y });

  const commitQuery = (q: string) =>
    setShallowSearchParams({ q: q.trim() || null });

  const clearAll = () => {
    setShallowSearchParams({ season: null, year: null, q: null });
    setQuery("");
  };

  const anyFilterActive =
    seasonFilter !== "all" || yearFilter !== "all" || qParam !== "";

  // Apply filters client-side -- no server round-trip on filter change
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (seasonFilter !== "all" && r.season_type !== seasonFilter) return false;
      if (yearFilter !== "all" && r.season_label !== yearFilter) return false;
      if (qParam && !r.destination_name.toLowerCase().includes(qParam.toLowerCase()))
        return false;
      return true;
    });
  }, [rows, seasonFilter, yearFilter, qParam]);

  const totalCrews = rows.length;

  // Group filtered rows by season_type in a fixed order
  const bySeason = useMemo(() => {
    const map = new Map<CrewSeasonType, DirectoryRow[]>();
    for (const s of SEASON_ORDER) map.set(s, []);
    for (const r of filtered) {
      if (map.has(r.season_type)) map.get(r.season_type)!.push(r);
    }
    return map;
  }, [filtered]);

  return (
    <main className="min-h-screen bg-warm-bg">
      {/* Hero */}
      <section className="relative bg-navy text-white pt-16 md:pt-28 pb-24 md:pb-32 px-5 md:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(28, 138, 153,0.08),transparent_70%)]" aria-hidden="true" />
        <div className="max-w-[1100px] mx-auto relative">
          <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold/80 mb-5">
            PeakWave · Crews
          </div>
          <h1 className="font-serif text-[44px] md:text-[72px] font-bold leading-[0.98] tracking-[-0.02em] mb-6 max-w-[780px]">
            Season Crews.
          </h1>
          <div className="w-12 h-[2px] bg-gold/40 mb-6" aria-hidden="true" />
          <p className="text-[16px] md:text-[19px] text-white/80 max-w-[680px] leading-[1.7]">
            Find your people for this season. See who&apos;s heading to your resort, yacht destination or festival, and connect before you arrive.
          </p>
          {totalCrews > 0 && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-8 md:mt-10 text-[13px] text-white/55">
              <span>{candidateCount.toLocaleString()} seasonaires</span>
              <span>{totalCrews} crews</span>
              <span>Free to join</span>
            </div>
          )}
        </div>
      </section>

      {/* Filter strip + content */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-12 -mt-12 md:-mt-16 relative z-10 pb-12 md:pb-16">
        {loadError ? (
          <div className="bg-white border border-border-light rounded-[18px] shadow-sm text-center py-14 md:py-20 px-5">
            <div className="font-serif text-[22px] md:text-[28px] font-bold text-navy mb-2 leading-[1.15]">
              We could not load crews right now.
            </div>
            <p className="text-[14px] text-text-muted mb-6 max-w-[440px] mx-auto">
              Something went wrong fetching the directory. Please try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-navy text-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold hover:bg-navy-hover transition-colors min-h-[44px] inline-flex items-center"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* Filter strip */}
            <div className="sticky top-0 z-20 bg-warm-bg/95 backdrop-blur rounded-[18px] border border-border-light shadow-sm pb-4 pt-4 px-5 mb-8">
              <div className="flex flex-wrap items-center gap-2">
                {/* Season type pills */}
                {(["all", "winter", "summer", "festivals"] as const).map((s) => (
                  <FilterPill
                    key={s}
                    active={seasonFilter === s}
                    onClick={() => setSeason(s)}
                  >
                    {SEASON_LABELS[s]}
                  </FilterPill>
                ))}

                {/* Separator */}
                <span className="mx-1 h-5 w-px bg-border-light hidden sm:block" aria-hidden />

                {/* Year filter */}
                {yearOptions.length > 1 && (
                  <select
                    value={yearFilter}
                    onChange={(e) => setYear(e.target.value)}
                    aria-label="Filter by season year"
                    className="bg-white border border-border-light rounded-full px-4 py-2 text-[13px] font-semibold text-navy focus:outline-none focus:border-gold/60 min-h-[44px]"
                  >
                    <option value="all">All years</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {/* Search */}
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onBlur={() => commitQuery(query)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitQuery(query);
                  }}
                  placeholder="Search destinations"
                  aria-label="Search destinations"
                  className="bg-white border border-border-light rounded-full px-4 py-2 text-[13px] placeholder:text-text-muted focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 min-h-[44px] min-w-[180px] flex-1 sm:flex-none"
                />

                {anyFilterActive && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="ml-1 text-[12.5px] font-semibold text-text-muted hover:text-navy underline-offset-4 hover:underline"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-14 md:py-20">
                <div className="font-serif text-[22px] md:text-[28px] font-bold text-navy mb-2 leading-[1.15]">
                  No crews yet for this filter.
                </div>
                <p className="text-[14px] text-text-muted mb-6 max-w-[440px] mx-auto">
                  Be the first. Create your profile and we&apos;ll match you with others heading the same way.
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link
                    href="/join"
                    className="bg-gold text-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold no-underline hover:bg-gold-light transition-colors min-h-[44px] inline-flex items-center"
                  >
                    Create your profile
                  </Link>
                  {anyFilterActive && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="border border-border-light bg-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold text-navy hover:border-gold/40 transition-colors"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Sections by season type */}
            {filtered.length > 0 && (
              <div className="space-y-14">
                {SEASON_ORDER.map((seasonType) => {
                  const items = bySeason.get(seasonType) ?? [];
                  if (items.length === 0) return null;
                  return (
                    <section key={seasonType} aria-label={`${SECTION_LABEL[seasonType]} crews`}>
                      {/* Only show section heading when "All" is selected or multiple sections visible */}
                      {(seasonFilter === "all") && (
                        <div className="mb-6">
                          <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold mb-3">{SECTION_LABEL[seasonType]} season</div>
                          <h2 className="font-serif text-[24px] md:text-[30px] font-bold text-navy leading-[1.15]">
                            {SECTION_LABEL[seasonType]}
                          </h2>
                        </div>
                      )}
                      <RegionalGroups items={items} />
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

// ─── Regional grouping ───────────────────────────────────────────────────────

function RegionalGroups({ items }: { items: DirectoryRow[] }) {
  const regionals = items
    .filter((r) => r.kind === "regional")
    .sort((a, b) => b.confirmed_count - a.confirmed_count);

  const nonRegional = items.filter((r) => r.kind !== "regional");

  // Map parent_crew_id → child rows
  const byParent = useMemo(() => {
    const map = new Map<string, DirectoryRow[]>();
    for (const r of nonRegional) {
      const k = r.parent_crew_id ?? "__orphan";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return map;
  }, [nonRegional]);

  // Determine which parent IDs are actually in the filtered regional list
  const regionalIds = useMemo(() => new Set(regionals.map((r) => r.crew_id)), [regionals]);

  // Orphans: non-regional rows whose parent is not in the filtered regional set
  const orphans = useMemo(
    () =>
      nonRegional.filter(
        (r) => !r.parent_crew_id || !regionalIds.has(r.parent_crew_id)
      ).sort((a, b) => b.confirmed_count - a.confirmed_count),
    [nonRegional, regionalIds]
  );

  return (
    <ul className="space-y-4" role="list">
      {regionals.map((reg) => {
        const children = (byParent.get(reg.crew_id) ?? []).sort(
          (a, b) => b.confirmed_count - a.confirmed_count
        );
        return (
          <li
            key={reg.crew_id}
            className="bg-white border border-border-light rounded-[18px] shadow-sm overflow-hidden hover:border-gold/40 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all"
          >
            <CrewCard row={reg} />
            {children.length > 0 && (
              <details className="border-t border-border-light">
                <summary className="list-none cursor-pointer px-5 py-3 text-[13px] font-semibold text-text-muted hover:text-navy flex items-center justify-between select-none min-h-[44px]">
                  <span>
                    View {children.length}{" "}
                    {children.length === 1 ? "destination" : "destinations"}
                  </span>
                  <span aria-hidden className="text-[16px] text-gold">
                    +
                  </span>
                </summary>
                <ul
                  className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border-light border-t border-border-light"
                  role="list"
                >
                  {children.map((c) => (
                    <li key={c.crew_id} className="bg-white">
                      <CrewCard row={c} compact />
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </li>
        );
      })}

      {/* Orphan resort/festival crews: no parent in the filtered set */}
      {orphans.map((c) => (
        <li
          key={c.crew_id}
          className="bg-white border border-border-light rounded-[18px] shadow-sm overflow-hidden hover:border-gold/40 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all"
        >
          <CrewCard row={c} />
        </li>
      ))}
    </ul>
  );
}

// ─── Crew card ───────────────────────────────────────────────────────────────

function CrewCard({
  row,
  compact = false,
}: {
  row: DirectoryRow;
  compact?: boolean;
}) {
  const padding = compact ? "px-5 py-4" : "px-5 py-5";
  const visibleCount = row.confirmed_count;

  return (
    <Link
      href={`/community/crew/${row.slug}`}
      className={`block ${padding} hover:bg-cream transition-colors no-underline text-charcoal group`}
      aria-label={`${row.destination_name} ${row.season_label} crew`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className={`font-serif font-bold text-navy leading-[1.2] truncate group-hover:text-gold transition-colors ${
              compact ? "text-[16px]" : "text-[20px] md:text-[22px]"
            }`}
          >
            {row.destination_name}
          </div>
          <div className="text-[12.5px] text-text-muted mt-0.5">{row.season_label}</div>
        </div>
        <span
          aria-hidden
          className="text-gold text-[18px] shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          &rarr;
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5">
        <span className="inline-flex items-center gap-1.5 text-[13px] text-navy font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-gold" aria-hidden />
          {visibleCount > 0
            ? `${visibleCount.toLocaleString()} ${visibleCount === 1 ? "seasonaire" : "seasonaires"}`
            : "Be first to join"}
        </span>
        {visibleCount > 0 && (
          <>
            <span className="text-border-light" aria-hidden>
              ·
            </span>
            <span className="text-[13px] text-text-muted">
              {visibleCount} confirmed
            </span>
          </>
        )}
        {visibleCount > 0 && row.top_role && (
          <span className="text-[11px] font-semibold uppercase tracking-[1.5px] text-gold bg-gold/8 px-2.5 py-0.5 rounded-full border border-gold/20">
            {row.top_role}
          </span>
        )}
      </div>
    </Link>
  );
}

// ─── Filter pill ─────────────────────────────────────────────────────────────

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[13px] font-semibold border transition-all min-h-[44px] ${
        active
          ? "bg-navy text-white border-navy shadow-sm"
          : "bg-white text-navy border-border-light hover:border-gold/40 hover:shadow-sm"
      }`}
    >
      {children}
    </button>
  );
}
